import {makeAutoObservable, observable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {LeaderboardType} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import {DateTime} from "luxon";
import {wait} from "data/utils";

export interface ILeaderboardsLayoutController extends ViewController {
	i18n: ILocalizationStore;

	get isRankingsLoading(): boolean;

	get lastUpdated(): string | null;

	get activeLeaderboard(): LeaderboardType;

	toggleLeaderboardTab: (value: string) => void;
	clearLastUpdated: () => Promise<void>;
}

@injectable()
export class LeaderboardsLayoutController implements ILeaderboardsLayoutController {
	@observable _lastUpdate: Date | null = null;

	private _disposers: Array<ReturnType<typeof reaction>> = [];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.RankingsStore) private readonly _rankingsStore: IRankingsStore
	) {
		makeAutoObservable(this);
	}

	get isRankingsLoading(): boolean {
		return this._rankingsStore.isLoading;
	}

	get lastUpdated(): string | null {
		if (!this._rankingsStore.lastUpdated) {
			return null;
			// const date = DateTime.fromJSDate( new Date());
			// return date.toFormat("h:mma ZZZZ");
		}
		const date = DateTime.fromJSDate(this._rankingsStore.lastUpdated);
		return date.toFormat("h:mma ZZZZ");
	}

	get activeLeaderboard(): LeaderboardType {
		return this._rankingsStore.activeLeaderboardType;
	}

	public toggleLeaderboardTab = (value: string): void => {
		const type = value as LeaderboardType;
		if (!type) {
			return;
		}

		this._rankingsStore.activeLeaderboardType = type;
	};

	init(param: void) {
		const lastUpdated = reaction(
			() => this._rankingsStore.lastUpdated,
			() => {
				void this.clearLastUpdated();
			}
		);
		this._disposers.push(lastUpdated);
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
	}

	clearLastUpdated = async () => {
		await wait(5000);
		this._rankingsStore.updateLastUpdated(null);
	};
}
